import {
  createLogin,
  createLogout,
  createSignup,
  getMe,
  listLinkedAccounts,
  switchAccount,
} from '../../generated/api/sdk.gen.js';
import type { AuthToken, LinkedAccount, User as GeneratedUser } from '../../generated/api/types.gen.js';
import type { AuthUserSettings } from '../../store/index.js';
import { unwrapGenerated } from './generated.js';

// ==========================================
// Auth Types
// ==========================================

export type UserStatus = 'pending' | 'approved' | 'rejected';

export type UserInfo = Omit<GeneratedUser, 'id' | 'username' | 'fullName' | 'status' | 'settings'> & {
  settings?: AuthUserSettings;
  id: string;
  email: string;
  username?: string;
  fullName?: string;
  status?: UserStatus;
  organizationId?: number;
  organizationName?: string | null;
  organizationPlan?: string | null;
};

export type { LinkedAccount };

/** A signed-in session: the access token and who it belongs to. The refresh token is in the httpOnly cookie. */
export type LoginResponse = Omit<AuthToken, 'user'> & {
  user: UserInfo & { permissions?: string[] };
};

function normalizeUser(user: GeneratedUser): UserInfo & { permissions?: string[] } {
  return {
    ...user,
    id: String(user.id),
    username: user.username ?? undefined,
    fullName: user.fullName ?? undefined,
    status: user.status as UserStatus,
    organizationId: user.organizationId ?? undefined,
    // Generated `settings` is `unknown` (free-form JSON column); the client only
    // reads the named keys in AuthUserSettings.
    settings: (user.settings ?? undefined) as AuthUserSettings | undefined,
  };
}

function normalizeAuthResponse(response: AuthToken): LoginResponse {
  return { ...response, user: normalizeUser(response.user) };
}

// ==========================================
// Auth API
// ==========================================

export const authApi = {
  async login(email: string, password: string): Promise<LoginResponse> {
    return normalizeAuthResponse(await unwrapGenerated(createLogin({ body: { email, password } })));
  },

  /**
   * Creates an account from an invitation: a valid `invitationToken` approves it and adds it to the inviting
   * organization. Without one the server refuses (sign-up is invitation-only).
   */
  async signup(
    email: string,
    password: string,
    username?: string,
    invitationToken?: string,
  ): Promise<{ user: UserInfo; message?: string }> {
    const response = await unwrapGenerated(createSignup({ body: { email, password, username, invitationToken } }));
    return { ...response, user: normalizeUser(response.user) };
  },

  async me(): Promise<{
    user: UserInfo & { permissions?: string[] };
  }> {
    const response = await unwrapGenerated(getMe());
    return { ...response, user: normalizeUser(response.user) };
  },

  /** Revokes the session's refresh token (the server reads it from the cookie) and clears the cookie. */
  async logout(): Promise<void> {
    await unwrapGenerated(createLogout());
  },

  async getLinkedAccounts(): Promise<LinkedAccount[]> {
    return unwrapGenerated(listLinkedAccounts());
  },

  async switchAccount(targetUserId: number): Promise<LoginResponse> {
    return normalizeAuthResponse(await unwrapGenerated(switchAccount({ body: { targetUserId } })));
  },
};
